import { useState } from 'react';
import type { Model, CameraRef } from '../../api';

interface ModelAttachModalProps {
  model: Model | null;
  cameras: CameraRef[];
  onAttach: (modelId: string, cameraId: string, config: unknown) => Promise<void> | void;
  onClose: () => void;
}

export default function ModelAttachModal({ model, cameras, onAttach, onClose }: ModelAttachModalProps) {
  const [selectedCamera, setSelectedCamera] = useState('');
  const [config, setConfig] = useState('{}');
  const [loading, setLoading] = useState(false);

  if (!model) return null;

  async function handleAttach() {
    if (!selectedCamera || !model) return;
    setLoading(true);
    try {
      let parsedConfig: unknown = {};
      try { parsedConfig = JSON.parse(config); } catch { /* use empty */ }
      await onAttach(model.id, selectedCamera, parsedConfig);
      onClose();
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overlay-backdrop">
      <div className="w-full max-w-md rounded-xl bg-card p-6 shadow-modal border border-border">
        <h3 className="text-[15px] font-semibold text-foreground">Attach "{model.name}" to Camera</h3>

        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-[13px] font-medium text-foreground">Select Camera *</label>
            <select
              value={selectedCamera}
              onChange={(e) => setSelectedCamera(e.target.value)}
              className="mt-1.5 w-full rounded-lg border border-border bg-input px-3 py-2 text-[13px] text-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring"
            >
              <option value="">Choose a camera</option>
              {(cameras || []).map((cam) => (
                <option key={cam.id} value={cam.id}>{cam.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[13px] font-medium text-foreground">Config (JSON)</label>
            <textarea
              value={config}
              onChange={(e) => setConfig(e.target.value)}
              rows={4}
              className="mt-1.5 w-full rounded-lg border border-border bg-input px-3 py-2 font-mono text-[13px] text-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-[13px] font-medium text-muted-foreground hover:bg-muted transition-colors">
            Cancel
          </button>
          <button
            onClick={handleAttach}
            disabled={loading || !selectedCamera}
            className="rounded-lg bg-brand px-4 py-2 text-[13px] font-semibold text-white hover:bg-brand-dark disabled:opacity-50 transition-colors"
          >
            {loading ? 'Attaching…' : 'Attach'}
          </button>
        </div>
      </div>
    </div>
  );
}
